<script setup lang="ts">
  import { decodeHtmlString } from '@/utils/htmlDecoding'
  import VgsButton from '@/components/common/VgsButton.vue'
  import { computedAsync, useClipboard } from '@vueuse/core'
  import { computed } from 'vue'
  import { codeToHtml } from 'shiki'

  const props = defineProps<{
    params: { language: string, line_numbers: string }
    code: string
    isInline?: boolean
  }>()

  // bumping up the contrast of the code comments making it accessible
  const replaceCodeCommentColor = (html: string) =>
    html.replaceAll('color:#6A737D', 'color:var(--color-gray-30);')

  const { copy, copied, isSupported } = useClipboard()
  const icon = computed(() => copied.value ? 'check' : 'copy')
  const codeAsString = decodeHtmlString(props.code)
  const codeAsHtml = computedAsync(async () => await codeToHtml(codeAsString, {
    lang: props.params.language,
    theme: 'github-dark',
  }).then(replaceCodeCommentColor))
</script>

<template>
  <code v-if="props.isInline" class="inline-code">{{ props.code }}</code>
  <div v-else class="code-block">
    <VgsButton
      v-if="isSupported"
      :icon-left="icon"
      class="copy-button"
      aria-label="Kopier kode"
      @click="copy(codeAsString)"
    />
    <div :class="{'line-numbers': props.params.line_numbers === 'on'}" v-html="codeAsHtml" />
  </div>
</template>

<style lang="scss" scoped>
  .code-block {
    position: relative;
    margin: 1rem 0;
    font-size: .90rem;

    :deep(.shiki) {
      margin: 0;
      padding: 1rem;
      border-radius: 4px;
      overflow-x: auto;
    }

    &:has(.line-numbers) {
      :deep(code) {
        counter-reset: step;
        counter-increment: step 0;
      }

      :deep(code .line::before) {
        content: counter(step);
        counter-increment: step;
        width: .90rem;
        margin-right: 1.5rem;
        display: inline-block;
        text-align: right;
        color: var(--color-gray-30)
      }
    }
  }

  .copy-button {
    position: absolute;
    top: 0;
    right: 0;
    border-radius: 0 4px;
    background-color: var(--color-gray-10);

    :deep(.ks-icon) {
      color: var(--color-blue-50);
    }
  }

  .inline-code {
    background-color: var(--color-gray-10);
    color: var(--color-black);
    padding: .25rem .5rem;
    border-radius: 4px;
    font-size: 1rem;
  }
</style>

